import { Button, Input, Text } from "@ttc3k/trekker";
import { useState } from "react";
import { Box, Flex, Grid } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { useOperatorByIdQuery } from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { CollapsableStep } from "widgets/booker/shared";
import { useCartContext, useInvoiceAdminBilling } from "widgets/booker/contexts";
import { InvoiceBillingFlyup } from "./InvoiceBillingFlyup";

export const InvoiceBillingStep = () => {
  const {
    recipientContact,
    setRecipientContact,
    showBillingFields,
    billingReadOnly,
  } = useInvoiceAdminBilling();
  const { selectedOperatorId, businessField } = useCartContext();

  const { data: operatorData } = useOperatorByIdQuery({
    variables: { operatorId: selectedOperatorId ?? "" },
    skip: !selectedOperatorId,
  });
  const linkedBusinessName = operatorData?.operatorById
    ? getMultilingualString(operatorData.operatorById.name ?? {})
    : businessField.businessName?.trim() || null;

  const { t } = useTranslation("booker", { keyPrefix: "INVOICE_BILLING" });
  const [billingFlyupOpen, setBillingFlyupOpen] = useState(false);

  const billingComplete = Boolean(
    recipientContact.email?.trim() && recipientContact.firstName?.trim(),
  );

  const displayName = [recipientContact.firstName, recipientContact.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <CollapsableStep title={t("TITLE")} complete={billingComplete}>
      <Text color="text.mid">{t("SUBTITLE")}</Text>

      {!billingReadOnly && !billingComplete && (
        <Box
          borderWidth="1px"
          borderColor="border.light"
          borderRadius="md"
          p="400"
          minH="120px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Button mode="light" onClick={() => setBillingFlyupOpen(true)}>
            {t("ADD_BILLING_INFO")}
          </Button>
        </Box>
      )}

      {(billingComplete || billingReadOnly) && (
        <Flex flexDir="column" gap="250">
          {!billingReadOnly && (
            <Flex flexDir="column" gap="150" alignItems="flex-start">
              <Flex flexDir="column" gap="50">
                {displayName ? (
                  <Text visual="bodySemiBold">{displayName}</Text>
                ) : null}
                {recipientContact.email ? (
                  <Text visual="smallRegular" color="text.mid">
                    {recipientContact.email}
                  </Text>
                ) : null}
              </Flex>
              <Button
                mode="light"
                size="sm"
                onClick={() => setBillingFlyupOpen(true)}
              >
                {t("CHANGE_BILLING")}
              </Button>
            </Flex>
          )}
          {linkedBusinessName && (
            <Flex
              flexDir="column"
              gap="50"
              borderWidth="1px"
              borderColor="border.light"
              borderRadius="md"
              px="200"
              py="150"
            >
              <Text visual="smallMedium" color="text.light">
                {t("BUSINESS")}
              </Text>
              <Text visual="bodySemiBold" color="text.dark">
                {linkedBusinessName}
              </Text>
            </Flex>
          )}

          {(showBillingFields || billingReadOnly) && (
            <Grid
              gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap="200"
            >
              <Input
                value={recipientContact.firstName}
                onChange={e =>
                  setRecipientContact(prev => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
                label={t("FORM.FIRST_NAME")}
                disabled={billingReadOnly}
              />
              <Input
                value={recipientContact.lastName}
                onChange={e =>
                  setRecipientContact(prev => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
                label={t("FORM.LAST_NAME")}
                disabled={billingReadOnly}
              />
              <Input
                value={recipientContact.email}
                onChange={e =>
                  setRecipientContact(prev => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                label={t("FORM.EMAIL")}
                type="email"
                disabled={billingReadOnly}
              />
              <Input
                value={recipientContact.phone}
                onChange={e =>
                  setRecipientContact(prev => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
                label={t("FORM.PHONE")}
                disabled={billingReadOnly}
              />
            </Grid>
          )}
        </Flex>
      )}

      <InvoiceBillingFlyup
        isOpen={billingFlyupOpen}
        onClose={() => setBillingFlyupOpen(false)}
      />
    </CollapsableStep>
  );
};
